import { Component } from '@angular/core';
import { Router } from '@angular/router';

@Component({
  selector: 'app-instrument',
  templateUrl: './instrument.component.html',
  styleUrls: ['./instrument.component.scss']
})
export class InstrumentComponent {
  featureItems = [
    {
      kicker: '智能编排',
      title: '智能排版，极致输出',
      description:
        '针对多页文档、固定版式与重复性任务，丹书可以自动建立稳定的页面结构，减少手工拖拽和重复调整，让批量输出更快进入生产环节。',
      bullets: ['统一管理页面结构与对象层级', '重复内容可快速复用，减少人工返工', '适合批量 PDF 编修与标准化出稿'],
      reverse: false,
      image: '../../assets/images/instrument/software_cover_1.png',
      imageAlt: '丹书智能排版界面',
      buttonAlign: 'end'
    },
    {
      kicker: '创意设计',
      title: '轻松设计，创意无限',
      description:
        '在保留 PDF 文档稳定性的基础上，支持文字、图形、图片等元素的灵活编辑，适合快速改稿、样张调整和视觉版本迭代。',
      bullets: ['支持图文混排与页面局部调整', '适合设计修订、样张确认与版本派生', '兼顾可视化编辑体验与文件精度'],
      reverse: true,
      image: '../../assets/images/instrument/software_cover_2.png',
      imageAlt: '丹书创意设计界面',
      buttonAlign: 'start'
    },
    {
      kicker: '多场景适配',
      title: '高效输出，箭矢呈现',
      description:
        '从简单单页内容到复杂多页资料，丹书都能保持一致的页面秩序与输出效率，帮助团队在修改频繁的场景中仍然稳定交付。',
      bullets: ['多页面任务保持统一版式节奏', '适配宣传页、说明书、包装附件等场景', '减少最终输出前的人工校对压力'],
      reverse: false,
      image: '../../assets/images/instrument/software_cover_3.png',
      imageAlt: '丹书高效输出界面',
      buttonAlign: 'end'
    },
    {
      kicker: '权限协作',
      title: '加密守护，随时分享',
      description:
        '面向团队协作场景，支持更清晰的文件流转方式。既能保护关键文档，也能让审阅、修改、导出过程更顺畅。',
      bullets: ['更适合内部审核与对外共享流程', '重要内容可按阶段控制输出与流转', '帮助设计、运营和生产团队协同工作'],
      reverse: true,
      image: '../../assets/images/instrument/software_cover_4.png',
      imageAlt: '丹书协作与安全界面',
      buttonAlign: 'start'
    }
  ];

  capabilityItems = [
    {
      title: '迁移与调用',
      description: '延续 PDF 工作流习惯，帮助团队快速切换到更清晰、更稳定的编辑方式。',
      icon: '../../assets/images/instrument/adv-1.svg',
      position: { x: 0, y: 0 },
      isHovered: false
    },
    {
      title: '复杂页面重构',
      description: '适合海量版式、重复结构和多版本文档的快速编修，减少重复劳动。',
      icon: '../../assets/images/instrument/adv-2.svg',
      position: { x: 0, y: 0 },
      isHovered: false
    },
    {
      title: '高效率输出',
      description: '把编辑、校对与输出衔接起来，缩短从文档修改到成品交付的路径。',
      icon: '../../assets/images/instrument/adv-3.svg',
      position: { x: 0, y: 0 },
      isHovered: false
    },
    {
      title: '文件压缩与优化',
      description: '在保证页面稳定性的前提下优化文件体积，更利于共享、存档与下游处理。',
      icon: '../../assets/images/instrument/adv-4.svg',
      position: { x: 0, y: 0 },
      isHovered: false
    },
    {
      title: '丰富的版式支持',
      description: '支持图文混排、多页内容组织和复杂版面管理，适用于更丰富的业务场景。',
      icon: '../../assets/images/instrument/adv-5.svg',
      position: { x: 0, y: 0 },
      isHovered: false
    },
    {
      title: '签名与安全性',
      description: '兼顾文件协作与内容保护，适合需要审阅、追踪和权限控制的企业流程。',
      icon: '../../assets/images/instrument/adv-6.svg',
      position: { x: 0, y: 0 },
      isHovered: false
    }
  ];

  private animationFrameId: number | null = null;
  private lightBallSize = 50;

  constructor(private router: Router) {}

  openTrialPage() {
    this.router.navigate(['/trial']);
  }

  openContactPage() {
    this.router.navigate(['/contact']);
  }

  onMouseEnter(index: number, event: MouseEvent) {
    const cardElement = event.currentTarget as HTMLElement;
    const cardRect = cardElement.getBoundingClientRect();
    const paddingX = parseFloat(getComputedStyle(cardElement).paddingLeft) + parseFloat(getComputedStyle(cardElement).paddingRight);
    const paddingY = parseFloat(getComputedStyle(cardElement).paddingTop) + parseFloat(getComputedStyle(cardElement).paddingBottom);

    this.capabilityItems[index].position.x = event.clientX - cardRect.left - (this.lightBallSize / 2) - (paddingX / 2);
    this.capabilityItems[index].position.y = event.clientY - cardRect.top - (this.lightBallSize / 2) - (paddingY / 2);
    this.capabilityItems[index].isHovered = true;
  }

  onMouseMove(event: MouseEvent, index: number) {
    const cardElement = event.currentTarget as HTMLElement;
    if (!cardElement) return;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => {
      const cardRect = cardElement.getBoundingClientRect();
      const paddingX = parseFloat(getComputedStyle(cardElement).paddingLeft) + parseFloat(getComputedStyle(cardElement).paddingRight);
      const paddingY = parseFloat(getComputedStyle(cardElement).paddingTop) + parseFloat(getComputedStyle(cardElement).paddingBottom);

      this.capabilityItems[index].position.x = event.clientX - cardRect.left - (this.lightBallSize / 2) - (paddingX / 2);
      this.capabilityItems[index].position.y = event.clientY - cardRect.top - (this.lightBallSize / 2) - (paddingY / 2);

      const multiple = 50;
      const calcX = (event.clientY - cardRect.top - cardRect.height / 2) / multiple;
      const calcY = -(event.clientX - cardRect.left - cardRect.width / 2) / multiple;
      cardElement.style.transform = `rotateX(${calcX}deg) rotateY(${calcY}deg)`;
    });
  }

  onMouseLeave(index: number) {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const cardElement = document.querySelectorAll('.instrument-adv-card')[index] as HTMLElement;
    if (cardElement) {
      cardElement.style.transform = 'rotateX(0) rotateY(0)';
    }

    this.capabilityItems[index].isHovered = false;
  }

}
